import { useEffect, useRef, useState } from 'react'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import {
  ArrowLeftIcon,
  ArrowsPointingOutIcon,
  MagnifyingGlassIcon,
  PaperClipIcon,
  PhotoIcon,
  PlusIcon,
} from '@heroicons/react/24/outline'
import { useAuth } from '../../providers/auth/useAuth'
import { AdManagementService, ChatbotService, type Attachment, type PexelsImage } from '../../client'
import { apiErrorMessage } from '../../lib/api'
import AttachmentPreview from './AttachmentPreview'
import MediaPreview from './MediaPreview'
import type { ComposerUploads } from './useComposerAttachments'

type View = 'menu' | 'search' | 'uploads'
type Status = '' | 'searching' | 'listing'

const SEARCH_DELAY = 400
const MIN_QUERY = 3

const MENU_ITEM =
  'flex w-full items-center gap-2.5 rounded-md px-3 py-2.5 text-left text-[13px] text-stone-700 transition-colors hover:bg-stone-50 disabled:opacity-50 md:py-2'
const TILE =
  'block aspect-square w-full overflow-hidden rounded-md bg-stone-100 hover:ring-2 hover:ring-purple-400 disabled:opacity-60'
const ZOOM =
  'absolute top-1 right-1 hidden rounded-full bg-stone-900/70 p-1 text-white transition hover:bg-stone-900 group-hover:block focus-visible:block pointer-coarse:block'
const NOTE = 'py-3 text-center text-[11px] text-stone-400'

type Props = {
  disabled?: boolean
  uploads: ComposerUploads
}

export default function AttachmentPicker({ disabled = false, uploads }: Props) {
  const { brand } = useAuth()
  const brandId = brand?.id
  const [view, setView] = useState<View>('menu')
  const [status, setStatus] = useState<Status>('')
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<PexelsImage[]>([])
  const [library, setLibrary] = useState<Attachment[]>([])
  const [preview, setPreview] = useState<string | null>(null)
  const [error, setError] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => clearTimeout(debounce.current ?? undefined), [])

  function search(next: string) {
    setQuery(next)
    setError('')
    clearTimeout(debounce.current ?? undefined)

    if (!brandId || next.trim().length < MIN_QUERY) {
      setResults([])
      setStatus('')
      return
    }

    setStatus('searching')
    debounce.current = setTimeout(async () => {
      try {
        const { images } = await AdManagementService.pexelsSearch(next.trim(), brandId)
        setResults(images ?? [])
      } catch (err) {
        setError(apiErrorMessage(err))
      } finally {
        setStatus('')
      }
    }, SEARCH_DELAY)
  }

  async function showUploads() {
    setView('uploads')
    setError('')
    if (!brandId) return

    setStatus('listing')
    try {
      const { assets } = await ChatbotService.listAssets(brandId)
      setLibrary(assets)
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setStatus('')
    }
  }

  return (
    <Popover>
      {({ close }) => (
        <>
          <PopoverButton
            disabled={disabled || uploads.full || !brandId}
            onClick={() => setView('menu')}
            aria-label="Add attachment"
            className="flex size-8 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30 sm:size-6"
          >
            <PlusIcon className="size-4 sm:size-3.5" />
          </PopoverButton>

          <PopoverPanel
            anchor={{ to: 'top start', gap: 6 }}
            className="z-50 flex w-64 flex-col rounded-xl border border-stone-200 bg-white shadow-[0_2px_4px_rgba(28,25,23,0.04),0_18px_44px_-16px_rgba(28,25,23,0.22)]"
          >
            {view === 'menu' ? (
              <div className="p-1.5">
                <button type="button" onClick={() => fileInputRef.current?.click()} className={MENU_ITEM}>
                  <PaperClipIcon className="size-4 text-stone-500" />
                  Add files and photos
                </button>
                <button type="button" onClick={() => setView('search')} className={MENU_ITEM}>
                  <MagnifyingGlassIcon className="size-4 text-stone-500" />
                  Search Image
                </button>
                <button type="button" onClick={showUploads} className={MENU_ITEM}>
                  <PhotoIcon className="size-4 text-stone-500" />
                  My Uploads
                </button>
                {error && <p className="px-3 py-1.5 text-[11px] text-red-600">{error}</p>}
              </div>
            ) : (
              <>
                <div className="flex items-center gap-1 border-b border-stone-200 px-2 py-1.5">
                  <button
                    type="button"
                    aria-label="Back"
                    onClick={() => setView('menu')}
                    className="rounded p-1 transition-colors hover:bg-stone-100"
                  >
                    <ArrowLeftIcon className="size-3.5 text-stone-500" />
                  </button>
                  <span className="text-[11px] font-medium tracking-wide text-stone-600 uppercase">
                    {view === 'search' ? 'Search Image' : 'My Uploads'}
                  </span>
                </div>

                <div className="max-h-72 overflow-y-auto p-2.5 [scrollbar-width:thin]">
                  {view === 'search' ? (
                    <div className="flex flex-col gap-2">
                      <input
                        autoFocus
                        value={query}
                        onChange={(event) => search(event.target.value)}
                        placeholder="Search images"
                        className="rounded-md border border-stone-200 px-2 py-1.5 text-[13px] outline-none focus:border-purple-400"
                      />
                      {status === 'searching' && <p className={NOTE}>Searching…</p>}
                      {status !== 'searching' && results.length > 0 && (
                        <div className="grid grid-cols-3 gap-1.5">
                          {results.map((image) => (
                            <div key={image.url} className="group relative">
                              <button
                                type="button"
                                title={`Photo by ${image.photographer}`}
                                onClick={() => {
                                  uploads.addPexels(image, `${query.trim()}.jpg`)
                                  close()
                                }}
                                className={TILE}
                              >
                                <img src={image.thumbnail} alt="" className="size-full object-cover" />
                              </button>
                              <button
                                type="button"
                                aria-label="Expand image"
                                onClick={() => setPreview(image.url)}
                                className={ZOOM}
                              >
                                <ArrowsPointingOutIcon className="size-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      {status === '' && query.trim().length >= MIN_QUERY && results.length === 0 && (
                        <p className={NOTE}>No results.</p>
                      )}
                    </div>
                  ) : status === 'listing' ? (
                    <p className={NOTE}>Loading…</p>
                  ) : library.length > 0 ? (
                    <div className="grid grid-cols-3 gap-1.5">
                      {library.map((attachment) => (
                        <div key={attachment.asset_id} className="group relative">
                          <button
                            type="button"
                            title={attachment.original_filename ?? undefined}
                            onClick={() => {
                              uploads.addSaved(attachment)
                              close()
                            }}
                            className={TILE}
                          >
                            <AttachmentPreview attachment={attachment} />
                          </button>
                          {attachment.kind === 'image' && (
                            <button
                              type="button"
                              aria-label="Expand image"
                              onClick={() => setPreview(attachment.url)}
                              className={ZOOM}
                            >
                              <ArrowsPointingOutIcon className="size-3" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className={NOTE}>No uploads yet.</p>
                  )}

                  {error && <p className="pt-2 text-center text-[11px] text-red-600">{error}</p>}
                </div>
              </>
            )}

            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={(event) => {
                const files = [...(event.target.files ?? [])]
                event.target.value = ''
                uploads.addFiles(files)
                close()
              }}
            />
          </PopoverPanel>

          <MediaPreview src={preview} onClose={() => setPreview(null)} />
        </>
      )}
    </Popover>
  )
}
